"use client"

import { useActionState, useEffect, useState } from "react"
import { useSearchParams } from "next/navigation"

import { AuthMode } from "@/components/auth/auth-mode"
import { routes } from "@/config/routes"
import { trackAuthMethodSelected } from "@/lib/analytics-actions"

interface UseAuthFormParams {
  googleOAuthUrl: string
  microsoftOAuthUrl: string
  slackOAuthUrl?: string
  onEmailSubmit: (
    formData: FormData
  ) => Promise<{ requiresMagicAuth?: boolean; error?: string | null }>
  onOAuthSubmit: (oauthUrl: string, provider: string) => Promise<void>
  error: string | null
  initialAuthMode: AuthMode
}

export function useAuthForm({
  googleOAuthUrl,
  microsoftOAuthUrl,
  slackOAuthUrl,
  onEmailSubmit,
  onOAuthSubmit,
  error,
  initialAuthMode,
}: UseAuthFormParams) {
  const isSignUp = initialAuthMode === AuthMode.SignUp

  const searchParams = useSearchParams()
  const toggleHref = (() => {
    const base = isSignUp ? routes.auth.signin() : routes.auth.signup
    const params = searchParams.toString()
    return params ? `${base}?${params}` : base
  })()

  // Banner visibility model: "show the error when it arrives; hide it when
  // the user starts typing". `isErrorHidden` is reset on every new submission
  // (inside the action wrapper) and whenever the parent surfaces a new error
  // value, so resubmitting and getting the same error message re-shows it.
  const [isErrorHidden, setIsErrorHidden] = useState(false)

  const [_state, formAction] = useActionState(
    async (
      _prevState: { requiresMagicAuth?: boolean; error?: string | null },
      formData: FormData
    ) => {
      const result = await onEmailSubmit(formData)
      setIsErrorHidden(false)
      return result
    },
    {
      error: null,
      requiresMagicAuth: false,
    }
  )

  useEffect(() => {
    if (error) setIsErrorHidden(false)
  }, [error])

  const rawError = _state.error ?? error
  const displayError = isErrorHidden ? null : rawError

  const authMode = isSignUp ? "signup" : "signin"

  const handleGoogleOAuth = async (e: React.MouseEvent) => {
    e.preventDefault()
    await trackAuthMethodSelected("google", authMode).catch(() => void 0)
    void onOAuthSubmit(googleOAuthUrl, "Google")
  }

  const handleMicrosoftOAuth = async (e: React.MouseEvent) => {
    e.preventDefault()
    await trackAuthMethodSelected("microsoft", authMode).catch(() => void 0)
    void onOAuthSubmit(microsoftOAuthUrl, "Microsoft")
  }

  const handleSlackOAuth = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!slackOAuthUrl) return
    await trackAuthMethodSelected("slack", authMode).catch(() => void 0)
    void onOAuthSubmit(slackOAuthUrl, "Slack")
  }

  const dismissError = () => {
    setIsErrorHidden(true)
  }

  return {
    isSignUp,
    toggleHref,
    formAction,
    displayError,
    dismissError,
    handleGoogleOAuth,
    handleMicrosoftOAuth,
    handleSlackOAuth,
  }
}
